import { useMediaQuery, useTheme } from "@mui/material";
import axios from 'axios'
import { enqueueSnackbar } from "notistack";
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from "react-router-dom";
import { API, IssueStatus } from "../../helpers/constants";
import { handleAxiosErrors, logoutUnauthorizedUser } from "../../helpers/errorHandling";
import { mapDateRange } from "../../helpers/helpers";
import { getRouteUrl } from "../../helpers/routing/getRouteUrl";
import { DASHBOARD } from "../../locales/pages/namespaces";
import { ROUTE_PAGE_ISSUES_EDIT } from "../../modules/issues/routes/routes";
import useUserAction from "../user/useUserAction";
import useUserData from "../user/useUserData";

export const sortingOptions = [
	{ name: 'TargetedEndDt', label: 'Targeted Expiry', direction: 'ascending' },
	{ name: 'CustName', label: 'Customer', direction: 'ascending' },
	{ name: 'Status', label: 'Status', direction: 'ascending' },
	{ name: 'PriorityOrder', label: 'Priority', direction: 'ascending' },
	{ name: 'SubmitDt', label: 'Submit Date', direction: 'descending' }
]

export const initialSortModel = [sortingOptions[0]]
export const initialFilterModel = [{
	isComplex: true,
	condition: 'and',
	ignoreAccent: false,
	predicates: [{
		field: 'StatusId',
		operator: 'isAnyOf',
		value: `${IssueStatus.AnamoniApodoxisKostous},${IssueStatus.EktimisiKostous},${IssueStatus.Anoixto},${IssueStatus.Anatethike},${IssueStatus.AnamoniAntallaktikon}`,
		isComplex: false,
		ignoreAccent: true,
		ignoreCase: true
	}]
}]
export const initialFilters = {
	customer: null,
	targetedStartDt: [],
	targetedEndDt: [],
	actualEndDt: [],
	submitDt: [],
	sites: [],
	erpCode: '',
	erpCode2: '',
	code: '',
	type: [],
	categories: [],
	assignees: [],
	internalAssignments: [],
	kind: [],
	status: []
}

const useDashboard = () => {
	const history = useHistory()
	const { t } = useTranslation(DASHBOARD)
	const { token, isLoggedIn } = useUserData();
	const { userLogout } = useUserAction();
	const theme = useTheme();
	const isBelowLg = useMediaQuery(theme.breakpoints.down('lg'));
	const [loading, setLoading] = useState(false)
	const [selectedIssueId, setSelectedIssueId] = useState(null)
	const [issues, setIssues] = useState({
		data: [],
		total: 0,
		page: 0,
		pageSize: 50,
		sortModel: initialSortModel,
		filterModel: initialFilterModel,
		dateParams: {}
	})
	const [filters, setFilters] = useState(initialFilters)
	const [orderByField, setOrderByField] = useState({field: 'TargetedEndDt', direction: 'ascending'})
	
	const issuesDataManager = useCallback(({
											   selection = null,
											   pageToGo = null,
											   newSortModel = null,
											   newFilterModel = null,
											   newDateParams = null
										   }) => {
		setLoading(true)
		const page = typeof pageToGo === 'number' ? pageToGo : issues.page
		const sortModel = newSortModel ? newSortModel : issues.sortModel
		const filterModel = newFilterModel ? newFilterModel : issues.filterModel
		const dateParams = newDateParams !== null ? newDateParams : issues.dateParams
		axios({
			method: 'post',
			headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
			url: `${API}/en/issues/index?skip=${page * issues.pageSize}&take=${issues.pageSize}&requiresCount=${true}`,
			data: {
				sorted: sortModel,
				where: filterModel,
				...dateParams
			}
		}).then(res => {
			setIssues((prevState) => ({
				...prevState,
				page: page,
				filterModel: filterModel,
				sortModel: sortModel,
				dateParams: dateParams,
				data: res.data.result,
				total: res.data.count,
			}))
			const selectionInList = selection && res.data.result.find(i => i.id === selection)
			setSelectedIssueId(selection && selectionInList ? selection : res.data.result[0]?.id)
			setLoading(false)
		}).catch(error => {
			enqueueSnackbar(handleAxiosErrors(error, t('An error occurred!')), {
				variant: 'error',
				autoHideDuration: 3000,
			})
			setLoading(false)
			logoutUnauthorizedUser(error, isLoggedIn, userLogout)
		})
	}, [t, issues.page, issues.sortModel, issues.filterModel, issues.dateParams, isLoggedIn, userLogout])
	
	useEffect(() => {
		issuesDataManager({ selection: null, pageToGo: null })
	}, [])
	
	const onIssueSelect = (issueId) => {
		if (isBelowLg) {
			history.push(getRouteUrl(ROUTE_PAGE_ISSUES_EDIT, { IssueId: issueId }))
		} else {
			setSelectedIssueId(issueId);
		}
	}
	
	const onPageChange = (event, page) => {
		issuesDataManager({ selection: null, pageToGo: page - 1 })
	}
	
	const onSortChange = (field, direction) => {
		setOrderByField({field: field, direction: direction})
		const newSortModel = sortingOptions.filter(item => item.name === field)?.map(i => ({...i, direction: direction}))
		issuesDataManager({ selection: null, pageToGo: null, newSortModel: newSortModel })
	}
	
	const onFilterChange = (updatedFilters) => {
		const predicates = []
		const dateParams = {}
		if(updatedFilters.status?.length > 0) {
			predicates.push({
				field: 'StatusId',
				operator: 'isAnyOf',
				value: updatedFilters.status.map(status => status.id)?.join(', '),
				isComplex: false,
				ignoreAccent: true,
				ignoreCase: true
			})
		} else {
			predicates.push({
				field: 'StatusId',
				operator: 'isAnyOf',
				value: `${IssueStatus.AnamoniApodoxisKostous},${IssueStatus.EktimisiKostous},${IssueStatus.Anoixto},${IssueStatus.Anatethike},${IssueStatus.AnamoniAntallaktikon}`,
				isComplex: false,
				ignoreAccent: true,
				ignoreCase: true
			})
		}
		if (updatedFilters.customer?.id) {
			predicates.push({
				field: 'CustId',
				operator: 'equals',
				value: updatedFilters.customer.id,
				isComplex: false,
				ignoreAccent: true,
				ignoreCase: true
			})
		}
		if (updatedFilters.sites?.length > 0) {
			predicates.push({
				field: 'AddrId',
				operator: 'isAnyOf',
				value: updatedFilters.sites.map(site => site.id)?.join(', '),
				isComplex: false,
				ignoreAccent: true,
				ignoreCase: true
			})
		}
		if (updatedFilters.type?.length > 0) {
			predicates.push({
				field: 'TypeId',
				operator: 'isAnyOf',
				value: updatedFilters.type.map(type => type.id)?.join(', '),
				isComplex: false,
				ignoreAccent: true,
				ignoreCase: true
			})
		}
		if (updatedFilters.categories?.length > 0) {
			predicates.push({
				field: 'ItemCategId',
				operator: 'isAnyOf',
				value: updatedFilters.categories.map(cat => cat.id)?.join(', '),
				isComplex: false,
				ignoreAccent: true,
				ignoreCase: true
			})
		}
		if (updatedFilters.assignees?.length > 0) {
			predicates.push({
				field: 'AssigneeId',
				operator: 'isAnyOf',
				value: updatedFilters.assignees.map(a => a.id)?.join(', '),
				isComplex: false,
				ignoreAccent: true,
				ignoreCase: true
			})
		}
		if (updatedFilters.internalAssignments?.length > 0) {
			predicates.push({
				field: 'InternalAssignmentId',
				operator: 'isAnyOf',
				value: updatedFilters.internalAssignments.map(a => a.id)?.join(', '),
				isComplex: false,
				ignoreAccent: true,
				ignoreCase: true
			})
		}
		if (updatedFilters.targetedStartDt?.length > 0) {
			const [from, to] = mapDateRange(updatedFilters.targetedStartDt)
			if (from) dateParams.TargetedStartDtFrom = from
			if (to) dateParams.TargetedStartDtTo = to
		}
		if (updatedFilters.targetedEndDt?.length > 0) {
			const [from, to] = mapDateRange(updatedFilters.targetedEndDt)
			if (from) dateParams.TargetedEndDtFrom = from
			if (to) dateParams.TargetedEndDtTo = to
		}
		if (updatedFilters.actualEndDt?.length > 0) {
			const [from, to] = mapDateRange(updatedFilters.actualEndDt)
			if (from) dateParams.ActualEndDtFrom = from
			if (to) dateParams.ActualEndDtTo = to
		}
		if (updatedFilters.submitDt?.length > 0) {
			const [from, to] = mapDateRange(updatedFilters.submitDt)
			if (from) dateParams.SubmitDtFrom = from
			if (to) dateParams.SubmitDtTo = to
		}
		if (updatedFilters.code?.trim()) {
			predicates.push({
				field: 'Code',
				operator: 'contains',
				value: updatedFilters.code?.trim(),
				isComplex: false,
				ignoreAccent: true,
				ignoreCase: true
			})
		}
		if (updatedFilters.erpCode2?.trim()) {
			predicates.push({
				field: 'ErpCode2',
				operator: 'contains',
				value: updatedFilters.erpCode2?.trim(),
				isComplex: false,
				ignoreAccent: true,
				ignoreCase: true
			})
		}
		if (updatedFilters.erpCode?.trim()) {
			predicates.push({
				field: 'ErpCode',
				operator: 'contains',
				value: updatedFilters.erpCode?.trim(),
				isComplex: false,
				ignoreAccent: true,
				ignoreCase: true
			})
		}
		if (updatedFilters.kind?.length > 0) {
			predicates.push({
				field: 'KindId',
				operator: 'isAnyOf',
				value: updatedFilters.kind.map(k => k.id)?.join(', '),
				isComplex: false,
				ignoreAccent: true,
				ignoreCase: true
			})
		}
		const model = predicates.length > 0 ? [{
			isComplex: true,
			condition: 'and',
			ignoreAccent: false,
			predicates: predicates
		}] : initialFilterModel
		issuesDataManager({
			selection: null,
			pageToGo: null,
			newSortModel: null,
			newFilterModel: model,
			newDateParams: dateParams
		})
	}
	
	return {
		loading,
		issues,
		selectedIssueId,
		onIssueSelect,
		issuesDataManager,
		onPageChange,
		isBelowLg,
		orderByField,
		setOrderByField,
		onSortChange,
		filters,
		setFilters,
		onFilterChange
	}
}

export default useDashboard;